/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get, map } from 'lodash';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { ButtonGroup, Dialog, MultiSelect, Typography2 } from '@coursera/cds-core';

import { compareByNaturalSortOrder } from 'bundles/author-common/utils/common';
import {
  type IdentifiableInterface,
  StaffManagementStoreV2,
  type StaffMembershipType,
  actions,
} from 'bundles/staff-management/src/store';
import { getRoles } from 'bundles/staff-management/src/utils/roleUtils';

import _t from 'i18n!nls/staff-management';

import { useNotifications } from '../common/notifications/Notifications';

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
  `,
  roles: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);

    .cds-selectController-outlined {
      max-width: unset;
    }

    .cds-selectController-itemsNumber {
      display: none;
    }
  `,
};

export default function EditRoleAssignmentDialog() {
  const [roles, setRoles] = React.useState<string[]>([]);
  const resetState = React.useCallback<() => void>(() => {
    setRoles([]);
  }, []);
  const executeAction = useFluxibleAction();
  const { notify } = useNotifications();
  const { partnerId, type, open, onClose, onConfirm, roleOptions, existingRoles } = useFluxibleStore<
    typeof StaffManagementStoreV2,
    {
      open: boolean;
      partnerId?: string | number | undefined;
      type?: StaffMembershipType;
      roleOptions: { label: string; id: string }[];
      existingRoles?: string[];
      onClose: () => void;
      onConfirm: (input: { roles: string[] }) => void;
    }
  >(StaffManagementStoreV2, (store) => {
    const templates = store.partnerRoleTemplates();
    return {
      partnerId: store.get().partnerId,
      type: store.get().type,
      open: Boolean(store.get().ui?.editFlow?.users?.length),
      existingRoles: store.get().ui?.editFlow?.roles,
      roleOptions: map(get(templates, 'templates'), (t) => ({
        label: t.name,
        id: t.id,
      })).sort((a, b) => compareByNaturalSortOrder(b.label.toLowerCase(), a.label.toLowerCase())),
      onClose: () => {
        executeAction(actions.editFlow.prompt, { users: [] });
        resetState();
      },
      onConfirm: (input: { roles: string[] }) => {
        executeAction(actions.editFlow.confirm, {
          fromId: store.get().fromId ?? '',
          type: store.get().type ?? 'partner',
          roles: input.roles,
          users: store.get().ui?.editFlow?.users as IdentifiableInterface[],
          notify,
        });
        resetState();
      },
    };
  });
  React.useEffect(() => {
    if (partnerId && type) {
      executeAction(actions.rolesOptions.load, { partnerId, type });
    }
  }, [executeAction, partnerId, type]);
  React.useEffect(() => {
    setRoles(existingRoles ?? []);
  }, [existingRoles]);
  const roleTemplates = getRoles();

  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.HeadingGroup>{_t('Edit Role Assignment')}</Dialog.HeadingGroup>
      <Dialog.Content css={styles.content}>
        <div css={styles.roles}>
          <Typography2 variant="subtitleMedium" component="label" htmlFor="role-select">
            {_t('Role(s)')}
          </Typography2>
          <MultiSelect
            placeholder={_t('Role(s)')}
            id="role-select"
            value={roles}
            onChange={(value) => setRoles(Array.from(value) as string[])}
          >
            {roleOptions.map(({ id, label }) => (
              <MultiSelect.Option key={id} supportText={roleTemplates[id]?.description}>
                {label}
              </MultiSelect.Option>
            ))}
          </MultiSelect>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: _t('Save'),
            onClick: () => onConfirm({ roles }),
            disabled: roles.length === 0,
          }}
          alternateButtonProps={{
            variant: 'secondary',
            children: _t('Cancel'),
            onClick: onClose,
          }}
        />
      </Dialog.Actions>
    </Dialog>
  );
}
